import { useCallback, useMemo, useState } from 'react';
import type { WeekDateRangeResponse } from '../api/generated';
import { useAppSelector } from '../store/hooks';
import { selectPatientsById } from '../store/patients/patientsSlice';
import {
  buildPatientsByPlanId,
  type PlanPatient
} from '../components/GanttChart/planPatients';
import {
  collectAlreadyStartedWeeks,
  countPlacedPlans,
  getSeriesStartWeek,
  MIN_PLANS_FOR_SCOPE_CHOICE,
  type RemovalAnchor,
  type RemovePlansScope
} from '../components/GanttChart/removePlansScope';
import { collectEditableSeries, collectGroupTreatmentPlanIds } from '../utils/ganttUtils';
import type { GanttGroup } from '../components/GanttChart/types';

// The row menu names the whole row before any dialog opens, so the dialog it
// raises must not offer to narrow that down again.
const ROW_MENU_SCOPE: RemovePlansScope = 'row';

// What the chart has to show before anything is removed. Removal takes one of
// two shapes, so the chart renders off the discriminant rather than juggling a
// flag per dialog:
// - 'scope'   → there is something to decide or something irreversible at stake
// - 'confirm' → nothing to decide, but patients would lose a booking
export type TreatmentPlanRemovalPrompt =
  | {
      kind: 'scope';
      group: GanttGroup;
      anchor: RemovalAnchor;
      /** Set when the entry point already fixed the scope. See ROW_MENU_SCOPE. */
      lockedScope?: RemovePlansScope;
    }
  | { kind: 'confirm'; treatmentPlanId: string; patients: PlanPatient[] };

// The same removal, held by the row it belongs to rather than by a snapshot of
// that row. The dialog has to keep describing the row as it is now: a plan that
// goes while the dialog is open must drop out of what confirming removes, and a
// row emptied underneath it must stop being described at all.
type PendingRemoval =
  | {
      kind: 'scope';
      templateId: string;
      anchor: RemovalAnchor;
      lockedScope?: RemovePlansScope;
    }
  | { kind: 'confirm'; treatmentPlanId: string; patients: PlanPatient[] };

interface UseTreatmentPlanRemovalParams {
  /** Every row in the chart. Both entry points name the row they act on. */
  groups: GanttGroup[];
  /** The year's week date ranges, used to tell which weeks have already started. */
  weeks: WeekDateRangeResponse[];
  onDeleteTreatmentPlan?: (treatmentPlanId: string) => void;
  onDeleteTreatmentPlans: (treatmentPlanIds: string[]) => void;
}

interface UseTreatmentPlanRemoval {
  /** The dialog the chart should render, or null when nothing is pending. */
  prompt: TreatmentPlanRemovalPrompt | null;
  patientsByPlanId: ReadonlyMap<string, PlanPatient[]>;
  /** Called when the user picks removal from a plan's menu. */
  requestPlanRemoval: (group: GanttGroup, treatmentPlanId: string) => void;
  /** Called when the user picks removal from the row label's menu. */
  requestRowRemoval: (group: GanttGroup) => void;
  /** Confirms a 'scope' prompt with the plans the dialog resolved. */
  confirmScopedRemoval: (treatmentPlanIds: string[]) => void;
  /** Confirms a 'confirm' prompt, which is always exactly one plan. */
  confirmSingleRemoval: () => void;
  cancelRemoval: () => void;
}

/**
 * Owns the year view's treatment plan removal flow for the whole chart: who a
 * removal affects, whether the user is asked to choose a scope, and what is
 * dispatched once they confirm.
 *
 * Both entry points — a plan's own menu and the row label's menu — share the one
 * pending state here. Two separate bits of pending state could each be set, and
 * a second entry point added later could quietly grow its own path around the
 * warning, which is exactly how the row menu came to delete started weeks in
 * silence.
 */
export function useTreatmentPlanRemoval({
  groups,
  weeks,
  onDeleteTreatmentPlan,
  onDeleteTreatmentPlans
}: UseTreatmentPlanRemovalParams): UseTreatmentPlanRemoval {
  const patientsById = useAppSelector(selectPatientsById);
  const isEditMode = useAppSelector((state) => state.applicationSlice.isEditMode);
  const [pending, setPending] = useState<PendingRemoval | null>(null);

  // A pending removal must not outlive the edit session it was raised in:
  // leaving edit mode releases the lock, so the dialog would sit over a chart
  // that can no longer be edited. The row used to remount on this signal and
  // drop the state with it; now that the state sits above the rows and is shared
  // by both entry points, the drop has to be said out loud.
  //
  // Adjusted during render rather than in an effect: the dialog has to be gone
  // in the same commit that leaves edit mode, and an effect would paint it once
  // more over a locked chart first.
  const [wasEditMode, setWasEditMode] = useState(isEditMode);
  if (wasEditMode !== isEditMode) {
    setWasEditMode(isEditMode);
    setPending(null);
  }

  const patientsByPlanId = useMemo(
    () => buildPatientsByPlanId(groups.flatMap((group) => group.series), patientsById),
    [groups, patientsById]
  );

  const prompt = useMemo<TreatmentPlanRemovalPrompt | null>(() => {
    if (pending === null) return null;
    if (pending.kind === 'confirm') return pending;

    const group = groups.find((g) => g.templateId === pending.templateId);
    // The row itself is gone, so there is no longer anything to describe or to
    // remove. The dialog closes rather than standing over a row that is not there.
    if (!group) return null;

    return pending.lockedScope === undefined
      ? { kind: 'scope', group, anchor: pending.anchor }
      : { kind: 'scope', group, anchor: pending.anchor, lockedScope: pending.lockedScope };
  }, [pending, groups]);

  const requestPlanRemoval = useCallback(
    (group: GanttGroup, treatmentPlanId: string) => {
      const series = group.series.find((s) => s.treatmentPlanId === treatmentPlanId);
      const startWeek = series ? getSeriesStartWeek(series) : null;

      // The dialog earns its place when there is either something to decide or
      // something irreversible at stake. Several placed plans in the row means a
      // scope to choose; a removal that reaches into weeks already started means
      // recorded history is about to go, and that must never happen on one click
      // regardless of how many plans the row holds.
      const hasScopeChoice =
        countPlacedPlans(collectEditableSeries(group)) >= MIN_PLANS_FOR_SCOPE_CHOICE;
      const destroysHistory =
        collectAlreadyStartedWeeks(group, [treatmentPlanId], weeks, new Date()).length > 0;

      if (startWeek !== null && (hasScopeChoice || destroysHistory)) {
        setPending({
          kind: 'scope',
          templateId: group.templateId,
          anchor: { kind: 'plan', treatmentPlanId, selectedStartWeek: startWeek }
        });
        return;
      }

      // Nothing to decide and nothing historical at risk, so removal stays the
      // immediate action it has always been, warning only about lost bookings.
      const patients = patientsByPlanId.get(treatmentPlanId) ?? [];
      if (patients.length > 0) {
        setPending({ kind: 'confirm', treatmentPlanId, patients });
        return;
      }

      onDeleteTreatmentPlan?.(treatmentPlanId);
    },
    [weeks, patientsByPlanId, onDeleteTreatmentPlan]
  );

  const requestRowRemoval = useCallback(
    (group: GanttGroup) => {
      // A row the planner cannot see into — a treatment type added this session
      // and never rolled out — holds no weeks, no bookings and no history. There
      // is nothing for the dialog to say, and it would say it about a count of
      // zero, so the row goes straight out. A row holding nothing but plans
      // carried over from the previous year has nothing of this year's to remove
      // either, so it skips the dialog with an empty id list.
      if (countPlacedPlans(collectEditableSeries(group)) === 0) {
        onDeleteTreatmentPlans(collectGroupTreatmentPlanIds(group));
        return;
      }

      setPending({
        kind: 'scope',
        templateId: group.templateId,
        anchor: { kind: 'row' },
        lockedScope: ROW_MENU_SCOPE
      });
    },
    [onDeleteTreatmentPlans]
  );

  const confirmScopedRemoval = useCallback(
    (treatmentPlanIds: string[]) => {
      if (pending?.kind !== 'scope') return;
      onDeleteTreatmentPlans(treatmentPlanIds);
      setPending(null);
    },
    [pending, onDeleteTreatmentPlans]
  );

  const confirmSingleRemoval = useCallback(() => {
    if (pending?.kind === 'confirm') onDeleteTreatmentPlan?.(pending.treatmentPlanId);
    setPending(null);
  }, [pending, onDeleteTreatmentPlan]);

  const cancelRemoval = useCallback(() => {
    setPending(null);
  }, []);

  return {
    prompt,
    patientsByPlanId,
    requestPlanRemoval,
    requestRowRemoval,
    confirmScopedRemoval,
    confirmSingleRemoval,
    cancelRemoval
  };
}
